import { addDays, addMonths, daysBetween, endOfMonth } from '@eis/core';
import type { InstanceApi } from '@eis/insuranceservice-sdk';

/**
 * The policy timeline's model: which periods the strip shows, and what each one says.
 *
 * A timeline reads left to right in business time. It must read as well for one period as for
 * twenty: the strip shows the period the page reads and up to two on either side, and says how
 * many more there are; the rest are in the expanded view. Each period says only what MATTERS
 * there — its dates and what began it, plus the tarif level and the status only where they begin
 * or change, and the fields and partners that changed at its start.
 */

/** One period of the timeline, as the strip draws it. */
export interface TimelineEntry {
  readonly validFrom: string;
  /** The exclusive end; empty when open-ended. */
  readonly validTo: string;
  /** CREATE, ENDORSE, CORRECT, TERMINATE, DETACH, UPGRADE — what began the period. */
  readonly operation: string;
  /** The bare status (`ACTIVE`). */
  readonly status: string;
  /** Shown only on the first period and where it differs from the period before. */
  readonly showStatus: boolean;
  /** `2026 · 1.3`; shown only on the first period and where the pinned tarif level changes. */
  readonly tarifLevel: string;
  readonly showTarifLevel: boolean;
  /** Catalog keys of the product fields that changed at the period's start. */
  readonly changedFields: readonly string[];
  readonly partnersChanged: boolean;
}

/** `tarifservice:fahrrad/2026/1.3` → `2026 · 1.3` (the generation and the level within it). */
export function tarifLevelOf(ern: string | undefined): string {
  const path = (ern ?? '').split(':')[1] ?? '';
  const [, generation, level] = path.split('/');
  if (generation === undefined || generation === '') return '';
  return level === undefined || level === '' ? generation : `${generation} · ${level}`;
}

const bareStatus = (status: string | undefined): string =>
  (status ?? '').replace(/^POLICY_STATUS_/, '') || 'UNSPECIFIED';

/**
 * The current periods, in business order, each with what it shows. A superseded belief is not a
 * period of the timeline (the audit view reads those).
 */
export function timelineEntries(
  slices: readonly InstanceApi.PolicySliceSummary[],
): TimelineEntry[] {
  const current = slices
    .filter((slice) => slice.current !== false)
    .slice()
    .sort((a, b) => (a.validFrom ?? '').localeCompare(b.validFrom ?? ''));
  return current.map((slice, index) => {
    const before = index === 0 ? undefined : current[index - 1];
    const status = bareStatus(slice.status);
    const tarifLevel = tarifLevelOf(slice.tarifVersionErn);
    return {
      validFrom: slice.validFrom ?? '',
      validTo: slice.validTo ?? '',
      operation: slice.operation ?? '',
      status,
      showStatus: before === undefined || bareStatus(before.status) !== status,
      tarifLevel,
      showTarifLevel:
        tarifLevel !== '' &&
        (before === undefined || tarifLevelOf(before.tarifVersionErn) !== tarifLevel),
      changedFields: before === undefined ? [] : (slice.changedFields ?? []),
      partnersChanged: before !== undefined && slice.partnersChanged === true,
    };
  });
}

/** The slice of the timeline the strip shows: indexes `[start, end)` and what lies outside. */
export interface TimelineWindow {
  readonly start: number;
  readonly end: number;
  readonly hiddenBefore: number;
  readonly hiddenAfter: number;
}

/**
 * The window around the period the page reads: `radius` periods on each side, shifted at the
 * ends so the strip stays full (the first period of twenty shows periods 1–5, not 1–3).
 */
export function timelineWindow(count: number, selected: number, radius = 2): TimelineWindow {
  if (count <= 0) return { start: 0, end: 0, hiddenBefore: 0, hiddenAfter: 0 };
  const size = Math.min(count, radius * 2 + 1);
  const anchor = Math.min(Math.max(selected, 0), count - 1);
  const start = Math.min(Math.max(anchor - radius, 0), count - size);
  const end = start + size;
  return { start, end, hiddenBefore: start, hiddenAfter: count - end };
}

/** The index of the period that contains `date` (a civil date), or -1. */
export function periodAt(entries: readonly TimelineEntry[], date: string): number {
  return entries.findIndex(
    (entry) => entry.validFrom <= date && (entry.validTo === '' || date < entry.validTo),
  );
}

/** A month boundary on the calendar scale. */
export interface ScaleTick {
  /** The first day of the month. */
  readonly date: string;
  /** Position on the scale, 0–100. */
  readonly at: number;
  /** Whether the month gets a label (every month, or every quarter on a long scale). */
  readonly labelled: boolean;
  /** Whether the year is printed here — January, and the first tick. */
  readonly yearLabelled: boolean;
}

/** One period on the calendar scale: where it starts and how wide it is, 0–100. */
export interface ScaleSegment {
  readonly index: number;
  readonly at: number;
  readonly width: number;
}

export interface TimelineScale {
  readonly ticks: readonly ScaleTick[];
  readonly segments: readonly ScaleSegment[];
  /** Today's position, 0–100. */
  readonly today: number;
}

/** Above this many months the scale labels quarters, not months. */
const MONTHLY_LABELS_UP_TO = 18;

/**
 * The calendar under the timeline, in proportion: from the first period's month to one month past
 * the later of the last period's start (or end) and today. An open-ended period runs to the end.
 */
export function timelineScale(entries: readonly TimelineEntry[], today: string): TimelineScale {
  const first = entries[0];
  const last = entries[entries.length - 1];
  if (first === undefined || last === undefined) return { ticks: [], segments: [], today: 0 };
  const start = `${first.validFrom.slice(0, 7)}-01`;
  const latest =
    [last.validTo === '' ? last.validFrom : addDays(last.validTo, -1), today].sort()[1] ?? today;
  const end = addDays(endOfMonth(addMonths(latest, 1)), 1);
  const total = Math.max(daysBetween(start, end), 1);
  const at = (date: string): number =>
    Math.min(Math.max((daysBetween(start, date) / total) * 100, 0), 100);

  const months: string[] = [];
  for (let month = start; month < end; month = addMonths(month, 1)) months.push(month);
  const quarterly = months.length > MONTHLY_LABELS_UP_TO;
  const ticks = months.map((date, index) => {
    const month = Number(date.slice(5, 7));
    return {
      date,
      at: at(date),
      labelled: !quarterly || (month - 1) % 3 === 0,
      yearLabelled: index === 0 || month === 1,
    };
  });
  const segments = entries.map((entry, index) => {
    const from = at(entry.validFrom);
    const to = at(entry.validTo === '' ? end : entry.validTo);
    return { index, at: from, width: Math.max(to - from, 0) };
  });
  return { ticks, segments, today: at(today) };
}
